import { useEffect, useRef } from "react";
import { type Id, Slide, toast } from "react-toastify";

import Typography from "@/design-system/Typography";
import Container from "@/design-system/container";
import { CloseIconSlimButton } from "@/design-system/icons/close-icon-slim";
import { useInvalidateQueryPageCustom } from "@/src/query-client-factory";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QuerySmMax, colors } from "../../styles/tokens.stylex";
import MillenniumBankSVG from "../common/icons/MillenniumBankSVG";
import type { IAccountSettings } from "./AccountSettings";
import { AccountSettings } from "./AccountSettings";
import GDPRFiles, { type IGDPRFile } from "./GDPRFiles";
import { type INewsletterSettings, NewsletterSettings } from "./NewsletterSettings";

import * as portalCss from "../shared/Portal.module.css";
import * as css from "./Settings.module.css";

export interface ISettings {
    accountSettings: IAccountSettings;
    newsletterSettings: INewsletterSettings;
    saveChangesSuccessMessage: string;
    myProfileMessage: string;
    newsletterMessage: string;
    files: IGDPRFile[];
    isBankUser: boolean;
    bankUser360BannerTitle: string;
}

export default function Settings(props: Readonly<ISettings>) {
    useInvalidateQueryPageCustom();
    const toastId = useRef<Id>(null);

    useEffect(
        () => () => {
            if (toastId.current) toast.dismiss(toastId.current);
        },
        [],
    );

    const showSettingsSuccess = (message: string) => {
        toast.dismiss(toastId.current || undefined);

        toastId.current = toast(
            ({ closeToast }) => {
                return (
                    <>
                        <CloseIconSlimButton styleX={styles.successMessageCloseIcon} onClick={closeToast} />
                        <Typography textElement="p" fontStyle="BodyL" styleX={styles.successMessageText}>
                            {message}
                        </Typography>
                    </>
                );
            },
            {
                autoClose: false,
                position: "bottom-center",
                closeButton: false,
                className: stylex.props(styles.successToastContainer).className,
                ariaLabel: "success-saved-changes",
                hideProgressBar: true,
                transition: Slide,
            },
        );
    };

    const settingsBox = `${css.settingsComponent} ${stylex.props(styles.border).className}`;

    return (
        <Container styleX={styles.settingsContainer}>
            {props.bankUser360BannerTitle && (
                <div className={css.banner}>
                    <div className={css.icon}>
                        <MillenniumBankSVG />
                    </div>
                    <span dangerouslySetInnerHTML={{ __html: props.bankUser360BannerTitle }} />
                </div>
            )}
            <div {...stylex.props(styles.account)}>
                <section className={settingsBox}>
                    <h1 className={portalCss.h3Font} data-testid="prof-pers-tittle">
                        {props.myProfileMessage}
                    </h1>
                    <AccountSettings {...props.accountSettings} showSettingsSuccess={showSettingsSuccess} />
                </section>
            </div>
            <div {...stylex.props(styles.newsletter)}>
                <section className={settingsBox}>
                    <h1 className={portalCss.h3Font} data-testid="prof-news-title">
                        {props.newsletterMessage}
                    </h1>
                    <NewsletterSettings
                        {...props.newsletterSettings}
                        showSettingsSuccess={showSettingsSuccess}
                    />
                </section>
            </div>
            {props.files?.length > 0 && (
                <div {...stylex.props(styles.gdpr)}>
                    <section className={settingsBox}>
                        <GDPRFiles files={props.files} />
                    </section>
                </div>
            )}
        </Container>
    );
}

const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    settingsContainer: {
        gap: "40px",
        gridTemplateAreas: {
            default: `
        "account newsletter"
        "account gdpr"`,
            [smMax]: `
        "account"
        "newsletter"
        "gdpr"`,
        },
        display: "grid",
        gridTemplateColumns: {
            default: "1fr 1fr",
            [smMax]: "1fr",
        },
    },

    gdpr: {
        gridColumnEnd: "gdpr",
        gridColumnStart: "gdpr",
        gridRowEnd: "gdpr",
        gridRowStart: "gdpr",
    },

    account: {
        gridColumnEnd: "account",
        gridColumnStart: "account",
        gridRowEnd: "account",
        gridRowStart: "account",
    },

    newsletter: {
        gridColumnEnd: "newsletter",
        gridColumnStart: "newsletter",
        gridRowEnd: "newsletter",
        gridRowStart: "newsletter",
    },

    successToastContainer: {
        borderRadius: ComposableSpacing.s2,

        gap: ComposableSpacing.s2,
        alignItems: "center",
        backgroundColor: "#1b2670",

        boxShadow: "0 3px 10px rgba(0, 0, 0, 0.1), 0 3px 3px rgba(0, 0, 0, 0.05)",

        flexDirection: "row-reverse",

        marginBottom: ComposableSpacing.s6,
        minHeight: "2rem",
        paddingBottom: ComposableSpacing.s3,
        paddingLeft: ComposableSpacing.s5,
        paddingRight: ComposableSpacing.s5,
        paddingTop: ComposableSpacing.s3,
        width: "90vw",
    },

    successMessageText: {
        flex: "1",
        color: colors.fg_AltDefault,
        textAlign: "center",
    },

    successMessageCloseIcon: {
        color: colors.fg_AltDefault,
        height: 20,
        width: 20,
    },

    border: {
        borderRadius: "1.5625rem",
    },
});
